import { Alert, LinearProgress, Paper, Stack, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useAppSelector } from "../../app/hooks";
import PageContainer from "../../components/common/PageContainer";
import { hasAnyPermission } from "../../features/auth/access";
import type { ExpenseScope } from "../../lib/api/expenses";
import { fetchExpensePeriod, saveTransportPeriod } from "../../lib/api/expensesTransport";
import { ExpensesFilterContext } from "./ExpensesFilterContext";
import ExpensesScopeBar from "./ExpensesScopeBar";
import {
    EXPENSE_AREA_META,
    EXPENSE_MONTHS,
    EXPENSE_SCOPE_ALL_STORES,
    EXPENSE_SCOPE_STORE,
    currentMonth,
    currentYear,
    type ExpenseAreaId,
} from "./expenses.constants";

function areaFromPath(pathname: string): ExpenseAreaId {
    if (pathname.includes("/marketing")) {
        return "marketing";
    }

    if (pathname.includes("/salaries")) {
        return "salaries";
    }

    return "transport";
}

export default function ExpensesLayout() {
    const location = useLocation();
    const user = useAppSelector((state) => state.auth.user);
    const area = areaFromPath(location.pathname);
    const canManage = hasAnyPermission(user, [
        `expenses.${area}.manage`,
        "expenses.manage",
        "dashboard.management",
    ]);
    const queryClient = useQueryClient();
    const meta = EXPENSE_AREA_META[area];
    const [scope, setScope] = useState<ExpenseScope>(EXPENSE_SCOPE_ALL_STORES);
    const [storeName, setStoreName] = useState("");
    const [year, setYear] = useState(currentYear());
    const [month, setMonth] = useState(currentMonth());
    const [statsMonths, setStatsMonths] = useState<number[]>([currentMonth()]);
    const [statsCustomized, setStatsCustomized] = useState(false);
    const [employeeName, setEmployeeName] = useState("");
    const [approvedByName, setApprovedByName] = useState("");
    const effectiveStoreName = scope === EXPENSE_SCOPE_STORE ? storeName : null;
    const statsLabel =
        statsMonths.length === 0
            ? "избери месеци"
            : statsMonths.length === 12
              ? "Цяла година"
              : statsMonths
                    .slice()
                    .sort((a, b) => a - b)
                    .map((item) => EXPENSE_MONTHS.find((month) => month.month === item)?.label ?? item)
                    .join(", ");

    const periodQuery = useQuery({
        queryKey: ["expenses-period", scope, effectiveStoreName, year, month],
        queryFn: () =>
            fetchExpensePeriod({
                scope,
                store: effectiveStoreName ?? undefined,
                year,
                month,
            }),
        enabled: scope !== EXPENSE_SCOPE_STORE || storeName !== "",
    });

    const storeOptions = periodQuery.data?.storeOptions ?? [];

    useEffect(() => {
        if (scope === EXPENSE_SCOPE_STORE && !storeName && storeOptions[0]) {
            setStoreName(storeOptions[0]);
        }
    }, [scope, storeName, storeOptions]);

    useEffect(() => {
        setEmployeeName(periodQuery.data?.periodMeta?.employeeName ?? "");
        setApprovedByName(periodQuery.data?.periodMeta?.approvedByName ?? "");
    }, [periodQuery.data?.periodMeta]);

    useEffect(() => {
        if (!statsCustomized) {
            setStatsMonths([month]);
        }
    }, [area, year, month, statsCustomized]);

    const periodSave = useMutation({
        mutationFn: () =>
            saveTransportPeriod({
                scope,
                store_name: effectiveStoreName,
                period_year: year,
                period_month: month,
                employee_name: employeeName || null,
                approved_by_name: approvedByName || null,
            }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expenses-period"] }),
    });

    const filterValue = useMemo(
        () => ({
            scope,
            storeName,
            year,
            month,
            statsMonths,
            effectiveStoreName,
            storeOptions,
            canManage,
            setScope,
            setStoreName,
            setYear,
            setMonth,
            setStatsMonths,
        }),
        [scope, storeName, year, month, statsMonths, effectiveStoreName, storeOptions, canManage],
    );

    return (
        <ExpensesFilterContext.Provider value={filterValue}>
            <PageContainer
                eyebrow="Разходи"
                title={meta.title}
                subtitle={`${meta.description} Месеци: ${statsLabel} ${year}.`}
            >
                {periodQuery.isFetching ? <LinearProgress sx={{ mb: 2, borderRadius: 999 }} /> : null}

                {scope === EXPENSE_SCOPE_STORE && storeName === "" && storeOptions.length > 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Изберете магазин от списъка, за да заредите и въведете разходи.
                    </Alert>
                ) : null}

                <Paper
                    elevation={0}
                    sx={{ p: 1, px: 1.5, mb: 1.5, borderRadius: 3, border: "1px solid rgba(148, 163, 184, 0.28)" }}
                >
                    <ExpensesScopeBar
                        scope={scope}
                        storeName={storeName || storeOptions[0] || ""}
                        storeOptions={storeOptions}
                        year={year}
                        statsMonths={statsMonths}
                        onScopeChange={setScope}
                        onStoreChange={setStoreName}
                        onYearChange={(nextYear) => {
                            setYear(nextYear);
                            setStatsCustomized(false);
                        }}
                        onStatsMonthsChange={(nextMonths, customized) => {
                            const sorted = nextMonths.slice().sort((a, b) => a - b);
                            setStatsMonths(nextMonths);
                            setStatsCustomized(customized);
                            if (sorted.length > 0) {
                                setMonth(sorted[0]);
                            }
                        }}
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1 }}>
                        <TextField
                            size="small"
                            label="Служител / отдел"
                            value={employeeName}
                            onChange={(event) => setEmployeeName(event.target.value)}
                            onBlur={() => canManage && periodSave.mutate()}
                            disabled={!canManage}
                            fullWidth
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            size="small"
                            label="Одобрил"
                            value={approvedByName}
                            onChange={(event) => setApprovedByName(event.target.value)}
                            onBlur={() => canManage && periodSave.mutate()}
                            disabled={!canManage}
                            fullWidth
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            size="small"
                            label="Изготвил"
                            value={periodQuery.data?.periodMeta?.preparedByName || user?.name || "—"}
                            disabled
                            fullWidth
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Stack>
                </Paper>

                <Suspense fallback={<LinearProgress sx={{ mb: 2, borderRadius: 999 }} />}>
                    <Outlet />
                </Suspense>

                {!canManage ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Имате права само за преглед. За въвеждане е нужно право „Управление на разходи“.
                    </Typography>
                ) : null}
            </PageContainer>
        </ExpensesFilterContext.Provider>
    );
}
